<template>
  <component :is="embedded ? 'div' : NewStudioModalsModalShell" v-bind="embedded ? {} : { title: galleryTitle }" data-figma-node="2850:170703" @close="$emit('close')">
    <button v-if="embedded" class="back" type="button" aria-label="Back to Start Designing" @click="$emit('back')"><img src="/new-studio/icons/modal-back.svg" alt=""></button>
    <p class="gallery-intro">Browse, pick, and customize your favorite.</p>
    <p v-if="error" role="alert">{{ error }}</p>
    <div class="gallery-grid">
      <article v-for="design in gallery.designs" :key="design.id" class="gallery-card" @click="openDesign(design.id)">
        <img v-if="design.image || design.shopify_image_url" :src="design.image || design.shopify_image_url" alt="">
        <div v-else class="placeholder"><img src="/new-studio/icons/image-placeholder.svg" alt=""><span>Drop image</span><small>or <u>browse files</u></small></div>
        <strong>{{ design.name || design.design_name || 'Gallery Design' }}</strong>
      </article>
    </div>
    <button v-if="gallery.currentPage < gallery.totalPages || gallery.isLoading" class="more" type="button" :disabled="gallery.isLoading" @click="loadMore">Load More</button>
  </component>
</template>

<script setup lang="ts">
import NewStudioModalsModalShell from './ModalShell.vue'
import { runGalleryRequest } from '../logic'

defineProps<{ embedded?: boolean }>()
const emit = defineEmits<{ close: []; back: [] }>()
const galleryTitle = "Felt Right's Gallery Designs"
const gallery = useGalleryStore()
const projectStore = useProjectStore()
const gridStore = useGridStore()
const loadingStore = useLoadingStore()
const error = ref('')
const loadGallery = async () => {
  await runGalleryRequest(
    () => gallery.loadDesigns(),
    error,
    'Unable to load gallery designs. Please try again.',
  )
  gallery.isLoading = false
}
const loadMore = async () => {
  const previousPage = gallery.currentPage
  const success = await runGalleryRequest(
    () => gallery.loadMore(),
    error,
    'Unable to load more gallery designs. Please try again.',
  )
  if (!success) gallery.currentPage = previousPage
  gallery.isLoading = false
}
const openDesign = async (id: string) => {
  const success = await runGalleryRequest(async () => {
    loadingStore.setLoading(true)
    const design = await projectStore.load(id)
    if (!design) throw new Error('Design not found')
    gridStore.resetGrid()
    await gridStore.loadDesign(design)
    setCookie('studio_design_id', id)
  }, error, 'Unable to load this design. Please try again.')
  loadingStore.setLoading(false)
  if (success) emit('close')
}
onMounted(() => { void loadGallery() })
</script>

<style scoped>
.gallery-intro { margin: 3px 0 27.203125px; text-align: center; color: #55554f; font-size: 16px; font-weight: 600; }
p[role='alert'] { color: #a32121; font-weight: 600; }
.back { position: absolute; z-index: 2; top: 40px; left: 40px; display: grid; width: 20px; height: 20px; padding: 0; place-items: center; border: 0; background: transparent; cursor: pointer; }
.back img { width: 7px; height: 12px; }
.gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px 22px; }
.gallery-card { display: grid; grid-template-rows: auto auto; cursor: pointer; }
article img, .placeholder { width: 100%; aspect-ratio: 1; object-fit: cover; background: #e5e4e0; border-radius: 4px; }
.placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; color: rgb(0 0 0 / 55%); font-family: Inter, sans-serif; }
.placeholder > img { width: 28px; height: 28px; margin-bottom: 5px; }
.placeholder span { font-size: 12.8px; line-height: 16.9px; letter-spacing: .13px; }
.placeholder small { font-size: 10.3px; line-height: 14.3px; }
.gallery-card strong { display: flex; align-items: center; padding: 10px 2px 6px; font-size: 14px; font-weight: 800; letter-spacing: -.085px; }
.more { width: 100%; min-height: 48px; margin-top: 20px; border: 0; background: #2e3a46; color: #fff; font: inherit; font-size: 18px; font-weight: 700; cursor: pointer; }
</style>
